/**
 * Catálogo de ícones (§3.7.2): desenhos nossos com primitivas SVG, sem pacote de ícones (AC-3.7-02). Cada ícone é a
 * lista literal das primitivas do catálogo; o componente Icon monta o <svg>.
 */

export type IconName =
  | 'search'
  | 'expand'
  | 'fit'
  | 'plus'
  | 'minus'
  | 'close'
  | 'chevron-left'
  | 'chevron-right'
  | 'caret-down'
  | 'share'
  | 'globe'
  | 'eye'
  | 'info'
  | 'play'
  | 'pause'
  | 'history';

export type IconPrimitive =
  | ['path', { d: string; 'stroke-width'?: number; opacity?: number }]
  | ['circle', { cx: number; cy: number; r: number; 'stroke-width'?: number; fill?: 'currentColor'; stroke?: 'none' }]
  | ['ellipse', { cx: number; cy: number; rx: number; ry: number; 'stroke-width'?: number }]
  | ['rect', { x: number; y: number; width: number; height: number; rx: number }];

export interface IconDef {
  viewBox: string;
  /** Traço padrão do ícone; null nos preenchidos (play, pause). */
  stroke: number | null;
  shapes: readonly IconPrimitive[];
}

const V16 = '0 0 16 16';

export const ICONS: Readonly<Record<IconName, IconDef>> = {
  search: {
    viewBox: V16,
    stroke: 1.6,
    shapes: [
      ['circle', { cx: 7.2, cy: 7.2, r: 4.7 }],
      ['path', { d: 'M10.7 10.7 13.8 13.8' }],
    ],
  },
  expand: {
    viewBox: V16,
    stroke: 1.5,
    shapes: [['path', { d: 'M2.5 6.5v-4h4M9.5 2.5h4v4M13.5 9.5v4h-4M6.5 13.5h-4v-4' }]],
  },
  fit: {
    viewBox: V16,
    stroke: 1.5,
    shapes: [['path', { d: 'M6.5 2.5v4h-4M13.5 6.5h-4v-4M9.5 13.5v-4h4M2.5 9.5h4v4' }]],
  },
  plus: { viewBox: V16, stroke: 1.6, shapes: [['path', { d: 'M8 3.5v9M3.5 8h9' }]] },
  minus: { viewBox: V16, stroke: 1.6, shapes: [['path', { d: 'M3.5 8h9' }]] },
  close: { viewBox: V16, stroke: 1.6, shapes: [['path', { d: 'M4.5 4.5l7 7M11.5 4.5l-7 7' }]] },
  'chevron-left': { viewBox: V16, stroke: 1.6, shapes: [['path', { d: 'M10 3.5 5.5 8l4.5 4.5' }]] },
  'chevron-right': { viewBox: V16, stroke: 1.6, shapes: [['path', { d: 'M6 3.5 10.5 8 6 12.5' }]] },
  'caret-down': {
    viewBox: '0 0 10 10',
    stroke: 1.5,
    shapes: [['path', { d: 'M2.2 3.8 5 6.6 7.8 3.8', opacity: 0.7 }]],
  },
  share: {
    viewBox: V16,
    stroke: 1.5,
    shapes: [
      ['path', { d: 'M8 9.8V2.6M5.2 5.2 8 2.4l2.8 2.8' }],
      ['path', { d: 'M3.8 8.2v4.6a1.2 1.2 0 0 0 1.2 1.2h6a1.2 1.2 0 0 0 1.2-1.2V8.2' }],
    ],
  },
  globe: {
    viewBox: V16,
    stroke: 1.3,
    shapes: [
      ['circle', { cx: 8, cy: 8, r: 6.25, 'stroke-width': 1.3 }],
      ['ellipse', { cx: 8, cy: 8, rx: 3, ry: 6.25, 'stroke-width': 1.1 }],
      ['path', { d: 'M1.75 8h12.5M2.95 5.1h10.1M2.95 10.9h10.1', 'stroke-width': 1 }],
    ],
  },
  eye: {
    viewBox: V16,
    stroke: 1.4,
    shapes: [
      ['path', { d: 'M1.2 8C3 4.7 5.3 3.2 8 3.2s5 1.5 6.8 4.8C13 11.3 10.7 12.8 8 12.8S3 11.3 1.2 8Z' }],
      ['circle', { cx: 8, cy: 8, r: 2.2, fill: 'currentColor', stroke: 'none' }],
    ],
  },
  info: {
    viewBox: V16,
    stroke: 1.4,
    shapes: [
      ['circle', { cx: 8, cy: 8, r: 6.2 }],
      ['path', { d: 'M8 7.2v4' }],
      ['circle', { cx: 8, cy: 4.9, r: 0.9, fill: 'currentColor', stroke: 'none' }],
    ],
  },
  play: { viewBox: V16, stroke: null, shapes: [['path', { d: 'M5 3.5v9l7.5-4.5Z' }]] },
  pause: {
    viewBox: V16,
    stroke: null,
    shapes: [
      ['rect', { x: 4, y: 3, width: 3, height: 10, rx: 0.8 }],
      ['rect', { x: 9, y: 3, width: 3, height: 10, rx: 0.8 }],
    ],
  },
  // FB-02 (ReplayLink): relógio com seta de volta
  history: {
    viewBox: V16,
    stroke: 1.4,
    shapes: [
      ['path', { d: 'M2.7 8a5.3 5.3 0 1 0 1.6-3.8M2.5 2.6v2.7h2.7' }],
      ['path', { d: 'M8 5.3V8l1.9 1.3' }],
    ],
  },
};

export const ICON_NAMES = Object.keys(ICONS) as IconName[];
